import { DateTime } from 'luxon';

/**
 * Rules of the campaign fields, shared by the manual creation wizard and the CSV import.
 * Each rule returns a stable error code (or null when valid), never a text: the caller maps
 * the code to its own i18n message. The backend applies the same rules to imported rows
 * (backend/src/services/campaigns/campaign-import-row-validator.ts); both sides run the
 * same table of cases (campaign-import-row-cases.json).
 */

const MAX_MONTHS_AHEAD = 2;
const ISO_WITH_OFFSET = /(Z|[+-]\d{2}(:?\d{2})?)$/i;

export type RequiredTextRuleCode = 'required';
export type UtmCampaignRuleCode = 'utmEmpty';
export type LinkRuleCode =
  | 'required'
  | 'websiteDomainNotConfigured'
  | 'linkInvalid'
  | 'linkNotFromStore';
export type DateRuleCode =
  | 'required'
  | 'dateInvalid'
  | 'dateInPast'
  | 'dateTooFar';
export type TimeRuleCode =
  | 'required'
  | 'timeIncomplete'
  | 'timeOutOfRange'
  | 'timeInPast';
export type LaunchDateTimeRuleCode =
  | 'required'
  | 'dateInvalid'
  | 'dateInPast'
  | 'dateTooFar'
  | 'timeInPast';

export const generateUtmFromName = (name: string) =>
  name
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

/** Name, notification title and notification body: only need to be non-empty. */
export function validateRequiredTextRule(
  value: string
): RequiredTextRuleCode | null {
  return value.trim() === '' ? 'required' : null;
}

export function validateUtmCampaignRule(
  value: string
): UtmCampaignRuleCode | null {
  return value.trim() === '' ? 'utmEmpty' : null;
}

/**
 * The link must be an absolute URL whose host is the store domain or a subdomain of it,
 * so the deep link always opens inside the store.
 */
export function validateLinkRule(
  link: string,
  storeWebsiteDomain: string
): LinkRuleCode | null {
  if (link.trim() === '') return 'required';
  if (storeWebsiteDomain.trim() === '') return 'websiteDomainNotConfigured';

  let hostname: string;
  try {
    hostname = new URL(link).hostname;
  } catch {
    return 'linkInvalid';
  }

  const domain = storeWebsiteDomain.toLowerCase();
  const isFromStore = hostname === domain || hostname.endsWith(`.${domain}`);
  return isFromStore ? null : 'linkNotFromStore';
}

/** `today` is the start of the current day in the same zone as `day`. */
function dayWindowCode(
  day: DateTime,
  today: DateTime
): 'dateInPast' | 'dateTooFar' | null {
  if (day < today) return 'dateInPast';
  if (day > today.plus({ months: MAX_MONTHS_AHEAD })) return 'dateTooFar';
  return null;
}

/** Wizard date picker: from today up to today plus 2 months. */
export function validateDateRule(
  date: Date | undefined,
  now: DateTime
): DateRuleCode | null {
  if (!date) return 'required';

  const dateTime = DateTime.fromJSDate(date);
  if (!dateTime.isValid) return 'dateInvalid';

  return dayWindowCode(dateTime.startOf('day'), now.startOf('day'));
}

/** Wizard time input (`HH:mm`), combined with the chosen date it cannot be in the past. */
export function validateTimeRule(
  timeStr: string,
  launchDate: Date | undefined,
  now: DateTime
): TimeRuleCode | null {
  if (!timeStr) return 'required';
  if (timeStr.length < 5) return 'timeIncomplete';

  const [hours, minutes] = timeStr.split(':');
  const time = DateTime.fromFormat(`${hours}:${minutes}`, 'HH:mm');
  if (!time.isValid) return 'timeOutOfRange';

  if (launchDate) {
    const launchDateTime = DateTime.fromJSDate(launchDate).set({
      hour: time.hour,
      minute: time.minute,
    });
    if (launchDateTime.diff(now).as('minutes') < 0) return 'timeInPast';
  }

  return null;
}

/**
 * A launch given as one ISO 8601 string with offset (what the import sends). "Today" is the
 * calendar day of `now` in the offset of the string, like the manual form uses the user's zone.
 */
export function validateLaunchDateTimeRule(
  value: string,
  now: DateTime
): LaunchDateTimeRuleCode | null {
  const trimmed = value.trim();
  if (trimmed === '') return 'required';

  const launch = DateTime.fromISO(trimmed, { setZone: true });
  if (!launch.isValid || !ISO_WITH_OFFSET.test(trimmed)) return 'dateInvalid';

  const dayCode = dayWindowCode(
    launch.startOf('day'),
    now.setZone(launch.zone).startOf('day')
  );
  if (dayCode) return dayCode;
  return launch < now ? 'timeInPast' : null;
}
